import React, { useState } from 'react';
import { Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import AvaliacaoDialog from '../components/AvaliacaoDialog';
import AulasDialog from '../components/AulasDialog';
import { Button } from '../components/ui/button';
import { mockAulasFeatured } from '../data/mock';

const energiaMap = {
  'pilates': 2,
  'yoga': 1,
  'hiit-burn': 3
};

const EnergyBadge = ({ level }) => (
  <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/55 backdrop-blur-sm px-2.5 py-1.5 z-10">
    {[1, 2, 3].map((i) => (
      <Zap
        key={i}
        size={14}
        strokeWidth={2.5}
        className={i <= level ? 'text-yellow-400' : 'text-gray-500'}
        fill={i <= level ? 'currentColor' : 'none'}
      />
    ))}
  </div>
);

const Aulas = () => {
  const [isAvalOpen, setIsAvalOpen] = useState(false);
  const [aulasDialog, setAulasDialog] = useState({ open: false, defaultAula: '' });

  const openAulasDialog = (aulaId = '') => setAulasDialog({ open: true, defaultAula: aulaId });
  const closeAulasDialog = () => setAulasDialog({ open: false, defaultAula: '' });

  return (
    <div className="min-h-screen bg-black">
      <Header onOpenForm={() => setIsAvalOpen(true)} />

      {/* Page Hero */}
      <PageHeader
        title="AULAS"
        subtitle="Treine em grupo com acompanhamento profissional e energia motivadora."
        backgroundImage="/assets/aulas-hero.webp"
        backgroundPosition="center 35%"
        overlayOpacity={0.55}
      />

      {/* Grid de Aulas */}
      <section className="py-16 sm:py-20 md:py-28 bg-black" data-testid="aulas-grid-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {mockAulasFeatured.map((aula) => (
              <article
                key={aula.id}
                data-testid={`aula-card-${aula.id}`}
                className="group flex flex-col"
              >
                {/* Imagem com overlay + indicador de energia */}
                <div className="relative overflow-hidden aspect-[3/4]">
                  <EnergyBadge level={energiaMap[aula.id] || 0} />
                  <img
                    src={aula.image}
                    alt={aula.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    style={{ objectPosition: 'center 30%' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/20 transition-opacity duration-500 group-hover:from-black/95" />
                </div>

                {/* Conteúdo abaixo da imagem */}
                <div className="pt-6 pb-4">
                  <h3
                    className="text-white uppercase mb-3"
                    style={{
                      fontFamily: "'BebasNeue-Book', sans-serif",
                      fontSize: "clamp(28px, 3vw, 40px)",
                      letterSpacing: "0.05em",
                      lineHeight: "1"
                    }}
                  >
                    {aula.title}
                  </h3>
                  <p
                    className="text-gray-300 mb-5"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 300,
                      fontSize: "0.95rem",
                      lineHeight: "1.6",
                      letterSpacing: "0.01em"
                    }}
                  >
                    {aula.description}
                  </p>
                  <button
                    onClick={() => openAulasDialog(aula.id)}
                    data-testid={`aula-info-btn-${aula.id}`}
                    className="btn-cut-corner"
                    style={{
                      fontFamily: "'BebasNeue-Book', sans-serif",
                      fontSize: "18px",
                      letterSpacing: "0.08em"
                    }}
                  >
                    SABER MAIS
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 md:py-20 bg-black border-t border-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2
            className="text-white uppercase mb-6"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontSize: "clamp(36px, 5vw, 64px)",
              letterSpacing: "0.05em",
              lineHeight: "1"
            }}
          >
            Pronto para começar?
          </h2>
          <p
            className="text-gray-300 mb-8 max-w-xl mx-auto"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 300,
              fontSize: "1rem",
              lineHeight: "1.6"
            }}
          >
            Garanta já a sua vaga numa das nossas aulas.
          </p>
          <Button
            onClick={() => openAulasDialog('')}
            data-testid="aulas-cta-btn"
            className="btn-mobile-cta bg-yellow-400 hover:bg-yellow-500 text-black uppercase px-10 py-6 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontWeight: "normal",
              fontSize: "33.6px",
              letterSpacing: "-0.02em"
            }}
          >
            <span style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>INSCREVER-ME AGORA</span>
          </Button>
        </div>
      </section>

      <Footer />
      <AvaliacaoDialog isOpen={isAvalOpen} onClose={() => setIsAvalOpen(false)} />
      <AulasDialog isOpen={aulasDialog.open} onClose={closeAulasDialog} defaultAula={aulasDialog.defaultAula} />
    </div>
  );
};

export default Aulas;
